import React, { useRef, useState } from 'react';
import { KBBlock } from '../../../types';
import { knowledgeBaseAPI } from '../../../api/services';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['pdf'].includes(ext)) return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return '🗜️';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return '🖼️';
  if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return '🎬';
  if (['mp3', 'wav', 'ogg'].includes(ext)) return '🎵';
  return '📎';
}

const FileAttachmentBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const fileUrl: string = block.data?.fileUrl ?? '';
  const fileName: string = block.data?.fileName ?? '';
  const fileSize: number = block.data?.fileSize ?? 0;

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError('');
    try {
      const url = await knowledgeBaseAPI.uploadFile(file, (pct) => setProgress(pct));
      onChange({
        data: {
          ...block.data,
          fileUrl: url,
          fileName: file.name,
          fileSize: file.size,
        },
      });
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  if (isEditing) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div
          className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-lg py-8 cursor-pointer hover:border-blue-300 transition-colors"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) handleUpload(f);
          }}
        >
          <svg className="w-8 h-8 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          <p className="text-sm text-gray-400">Click or drag a file to upload</p>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
        </div>

        {uploading && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Uploading…</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {error && <p className="text-xs text-red-500">{error}</p>}

        {fileUrl && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <span className="text-xl">{getFileIcon(fileName)}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{fileName}</p>
              {fileSize > 0 && <p className="text-xs text-gray-400">{formatBytes(fileSize)}</p>}
            </div>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex-shrink-0">View</a>
          </div>
        )}

        <button onClick={onFinish} className="text-xs bg-blue-600 text-white px-3 py-1 rounded">Done</button>
      </div>
    );
  }

  if (!fileUrl) {
    return (
      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-dashed border-gray-200">
        <span className="text-gray-300 italic text-sm">No file attached</span>
      </div>
    );
  }

  return (
    <a
      href={fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
    >
      <span className="text-2xl">{getFileIcon(fileName)}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700 truncate group-hover:text-blue-700">{fileName || 'Attachment'}</p>
        {fileSize > 0 && <p className="text-xs text-gray-400">{formatBytes(fileSize)}</p>}
      </div>
      <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    </a>
  );
};

export default FileAttachmentBlock;
